import {useEffect, useState} from 'react'
import {useLocation} from 'react-router-dom'
import {useQuery} from '@tanstack/react-query'
import {useAccessToken, useCustomerType} from '@salesforce/commerce-sdk-react'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useOptionalBrand, toBrandCode} from '../contexts/brand-context'
import {buildDecisionSearchParams, normalizeLoyaltyTier} from '../utils/optimizely-attributes'
import {getFxAudiencePage, readOptlyQa} from '../utils/optimizely-fx-page-context'
import {getVisitorId} from '../utils/optimizely-visitor-id'
import {getVisitorContext} from '../utils/optimizely-visitor-context'
import {useSsrFetchOrigin} from './use-ssr-fetch-origin'

/**
 * One silent decision read per (visitor, route, optly_qa) tuple, shared by
 * every useFeatureFlag call. Disabled until a caller asks for experiment mode
 * and a stable visitor id exists — anonymous shoppers must not be bucketed as
 * `anonymous` and then again as their UUID.
 *
 * Headers match /cms-optimizely-decisions: x-visitor-id and x-visitor-context, so
 * fx-* flags share the project's audiences. The same queryString is reused by
 * POST /feature-flag-impression.
 */
export const useFeatureFlagDecisions = (enabled) => {
    const {site, locale} = useMultiSite()
    const brandId = useOptionalBrand()?.brandId || 'garage'
    const {pathname, search} = useLocation()
    const page = getFxAudiencePage(pathname)
    const optlyQa = readOptlyQa(search)
    const localeId = locale?.id || locale?.alias || 'en-CA'
    const brandSlug = `${toBrandCode(brandId).toLowerCase()}-web`
    const siteId = site?.id
    const fetchOrigin = useSsrFetchOrigin()

    const {token: accessToken} = useAccessToken()
    const {isRegistered} = useCustomerType()
    const [hydrated, setHydrated] = useState(false)
    useEffect(() => setHydrated(true), [])
    const effectiveIsRegistered = hydrated ? !!isRegistered : false
    const visitorId = hydrated ? getVisitorId() : null

    const {data: customer} = useCurrentCustomer()
    const customerGroupsCsv =
        hydrated && Array.isArray(customer?.c_customerGroups)
            ? customer.c_customerGroups.join(',')
            : ''
    const loyaltyTier =
        hydrated && effectiveIsRegistered
            ? normalizeLoyaltyTier(customer?.c_loyaltyMostRecentTier)
            : ''

    const headers = {'x-is-logged-in': effectiveIsRegistered ? 'true' : 'false'}
    if (accessToken) headers.authorization = `Bearer ${accessToken}`
    if (customerGroupsCsv) headers['x-customer-groups'] = customerGroupsCsv
    if (loyaltyTier) headers['x-loyalty-tier'] = loyaltyTier
    if (visitorId) headers['x-visitor-id'] = visitorId
    if (hydrated) headers['x-visitor-context'] = JSON.stringify(getVisitorContext())

    const queryString = buildDecisionSearchParams({
        brand: brandSlug,
        locale: localeId,
        pathname: page.pathname,
        pageType: page.page_type,
        optlyQa
    }).toString()

    const query = useQuery({
        queryKey: [
            'feature-flag-decisions',
            siteId,
            brandSlug,
            localeId,
            visitorId,
            effectiveIsRegistered,
            customerGroupsCsv,
            loyaltyTier,
            page.pathname,
            page.page_type,
            optlyQa
        ],
        enabled: Boolean(enabled) && hydrated && !!visitorId,
        staleTime: Infinity,
        gcTime: 30 * 60 * 1000,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        queryFn: async () => {
            const response = await fetch(`${fetchOrigin}/feature-flag-decisions?${queryString}`, {
                headers
            })
            if (!response.ok) throw new Error('Feature flag decisions failed')
            const json = await response.json()
            return json?.decisions && typeof json.decisions === 'object' ? json.decisions : {}
        }
    })

    const idle = {decisions: {}, headers, fetchOrigin, queryString}
    if (!enabled) return {status: 'idle', ...idle}
    if (!hydrated || !visitorId || query.isLoading || query.isFetching)
        return {status: 'pending', ...idle}
    if (query.isError) return {status: 'unavailable', ...idle}
    if (!query.isSuccess) return {status: 'pending', ...idle}
    return {status: 'ready', decisions: query.data || {}, headers, fetchOrigin, queryString}
}
